/**
 * Class-agnostic upload queue primitive shared by chat, workspace, and
 * canvas adapters. Owns:
 *
 * - tempId minting + abort controllers per item
 * - the `pendingCancellations` ref (dedupes cancel-before-create-resolves)
 * - the per-id status subscription that mirrors `file_record.*` SSE
 *   updates back into queue state
 * - the cancel/cleanup decision matrix (DELETE the row for cancelled
 *   `create` items; never DELETE a `replace` item's row — the row belongs
 *   to the user's existing file)
 *
 * Per-surface adapters (chat / workspace / canvas) project queue state
 * into their own UI shapes and add surface-specific UX (send-gate,
 * ghost dismissal, optimistic Loro placement, etc.) on top.
 */

import { bucketByteSize } from "@/analytics/hygiene";
import type { UploadMethod } from "@/analytics/surfaces";
import { orgAPI } from "@/api/api";
import { fileRecordKeys, useFileRecordsByIds } from "@/api/fileRecords";
import {
  describeOversizedFile,
  partitionFilesBySizeLimit,
} from "@/constants/file-limits";
import Sentry from "@/integrations/Sentry";
import {
  trackFileUploadCompleted,
  trackFileUploadFailed,
  trackFileUploadStarted,
} from "@/utils/analytics";
import { withUploadSlot } from "@/utils/uploadConcurrency";
import type { FileRecord } from "@cohesive/api-client";
import { pickFileRecordMediaURL } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import mime from "mime-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  listExistingNamesForTarget,
  performFileRecordUpload,
  type UploadTarget,
} from "./useFileRecordUpload";

export type UploadQueueItemStatus =
  | "initializing"
  | "pending"
  | "processing"
  | "processed"
  | "failed";

export interface UploadQueueItem {
  /** Client-only React key. Never collides with a server id. */
  tempId: string;
  /** The raw File; retained through upload so image previews can
   *  `URL.createObjectURL` it without a network round-trip. */
  file: File;
  name: string;
  /**
   * Destination parent directory of the upload (`""` for workspace
   * root, `"foo/"` for a subfolder). Derived from `resolvedPath` at
   * enqueue time, so consumers like the workspace ghost-tile renderer
   * can filter the projected queue down to items destined for the
   * folder the user is currently viewing — without that, a folder
   * drop briefly shows its inner files at the workspace root before
   * they slot into their actual parent. Undefined when the upload
   * has no resolved path (canvas / flow-session targets that don't
   * carry a folder concept).
   */
  destinationParentPath?: string;
  content_type: string;
  size: number;
  width?: number;
  height?: number;
  status: UploadQueueItemStatus;
  /** Server id from `POST /file-records`. Absent while the create
   *  request is still in flight. */
  fileRecordId?: string;
  /** True after the presigned S3 POST succeeds (object bytes landed). */
  s3Posted?: boolean;
  /**
   * "create" reserves a brand-new FileRecord row and S3 object — cancelling
   * means we should DELETE that row. "replace" reuses an existing row for
   * an overwrite, so cancelling must NOT delete the row or we'd destroy
   * the user's original file.
   */
  kind: "create" | "replace";
  /**
   * An already-existing record attached by id (rail drag, Add from workspace)
   * rather than a record this queue created. Removal only drops the chip —
   * the queue owns nothing server-side, and deleting would archive the
   * user's workspace file.
   */
  existingAttachment?: boolean;
  /**
   * Server-side preview for attached existing records. Their `file` is a
   * zero-byte placeholder, so the chip cannot blob-URL it like an upload's.
   */
  previewUrl?: string;
  abortController: AbortController;
  error?: string;
}

/**
 * One file to enqueue, optionally with a pre-resolved destination from
 * the conflict resolver. When `resolvedPath` is set the queue skips its
 * own collision-rename pass and uses the path directly; `kind` controls
 * cancellation cleanup.
 *
 * `meta` is passed back unchanged through every callback so callers can
 * pair queue events with their own per-file state (e.g. a canvas drop's
 * ghost id, or a workspace upload's intent token).
 */
export interface EnqueueFile<TMeta = unknown> {
  file: File;
  resolvedPath?: string;
  displayName?: string;
  contentType?: string;
  kind?: "create" | "replace";
  meta?: TMeta;
}

export interface EnqueueCallbackContext<TMeta = unknown> {
  /** The queue's internal tempId for this item. */
  tempId: string;
  /** Caller-supplied metadata threaded through from `EnqueueFile.meta`. */
  meta?: TMeta;
}

export interface EnqueueOptions<TMeta = unknown> {
  /**
   * Fired once per file as soon as `POST /file-records` returns the row,
   * before the S3 POST. Used by canvas-scoped flows to swap a client-local
   * ghost for a real Loro node with minimal latency — the caller doesn't
   * have to wait for `processed` status to react.
   */
  onRecordCreated?: (
    record: FileRecord,
    ctx: EnqueueCallbackContext<TMeta>,
  ) => void;
  /** Fired immediately after the S3 POST resolves. */
  onUploaded?: (record: FileRecord, ctx: EnqueueCallbackContext<TMeta>) => void;
  /**
   * Per-file failure (network error, S3 4xx/5xx, abort by code path).
   * The matching queue item is already flagged `failed` by the time this
   * fires; surfaces use it to surface toasts / remove optimistic UI.
   */
  onUploadError?: (
    err: unknown,
    ctx: EnqueueCallbackContext<TMeta> & { record?: FileRecord },
  ) => void;
  /** Upload gesture for analytics (drop, picker, composer, …). */
  method?: UploadMethod;
}

export function useFileRecordUploadQueue() {
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const queryClient = useQueryClient();

  /**
   * `tempId`s the user cancelled before `POST /file-records` returned.
   * When the create response eventually lands we check this set and
   * DELETE the freshly-minted row rather than proceeding with the S3
   * POST. Stored in a ref so cancel decisions made between renders
   * don't get stale-closured by the async loop in `enqueue`.
   */
  const pendingCancellations = useRef<Set<string>>(new Set());

  /**
   * Latest-queue ref so `enqueue` can see every in-flight claim
   * without pulling `queue` into its dep array (which would recreate
   * the callback on every queue change and cascade through downstream
   * hook re-registration).
   */
  const queueRef = useRef<UploadQueueItem[]>([]);
  queueRef.current = queue;

  // Per-id subscription. `fileRecordKeys.detail(id)` is written live by
  // the SSE handler, so mirroring the data back into queue state makes
  // status transitions (pending → processed) propagate to consumers
  // with no polling.
  const recordIds = useMemo(
    () => queue.map((q) => q.fileRecordId).filter((id): id is string => !!id),
    [queue],
  );
  const recordQueries = useFileRecordsByIds(recordIds);

  // `useQueries` hands back a fresh array reference on every render. Squash
  // the array down to a stable `id|status` signature so the effect only
  // fires when actual status data changes, not on every render.
  const recordStatusSignature = useMemo(
    () =>
      recordQueries
        .map((q) => (q.data ? `${q.data.id}:${q.data.status}` : ""))
        .filter(Boolean)
        .sort()
        .join(","),
    [recordQueries],
  );

  useEffect(() => {
    if (!recordStatusSignature) return;
    const statusById = new Map<string, FileRecord["status"]>();
    for (const entry of recordStatusSignature.split(",")) {
      const [id, status] = entry.split(":");
      if (id && status) {
        statusById.set(id, status as FileRecord["status"]);
      }
    }
    if (statusById.size === 0) return;

    setQueue((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (!item.fileRecordId) return item;
        const s = statusById.get(item.fileRecordId);
        if (!s) return item;
        // Map server status onto the queue status. Any "processing" /
        // "processed" coming off the wire wins over the local value
        // because the server is the source of truth once the record
        // exists.
        const mapped: UploadQueueItemStatus =
          s === "processed"
            ? "processed"
            : s === "processing"
              ? "processing"
              : "pending";
        if (mapped === item.status) return item;
        changed = true;
        return { ...item, status: mapped };
      });
      return changed ? next : prev;
    });
  }, [recordStatusSignature]);

  const enqueue = useCallback(
    async <TMeta = unknown>(
      files: EnqueueFile<TMeta>[],
      target: UploadTarget,
      options?: EnqueueOptions<TMeta>,
    ) => {
      if (files.length === 0) return;

      // Per-file size cap. Mirrors workspace's pre-existing behaviour:
      // toast oversized files, continue with the rest. Applied here so
      // every Pattern A surface gets the same UX; previously chat and
      // canvas would have hit S3's `content-length-range` policy and
      // got a generic 403 instead.
      const rawFiles = files.map((f) => f.file);
      const { accepted, rejected } = partitionFilesBySizeLimit(rawFiles);
      for (const over of rejected) {
        toast.error(describeOversizedFile(over));
      }
      if (accepted.length === 0) return;
      const acceptedSet = new Set(accepted);
      const acceptedFiles = files.filter((f) => acceptedSet.has(f.file));

      trackFileUploadStarted({
        count: acceptedFiles.length,
        sizeBucket: bucketByteSize(
          acceptedFiles.reduce((sum, f) => sum + f.file.size, 0),
        ),
        destinationClass: target.class,
        method: options?.method,
      });

      // For non-resolved enqueues, prefetch the destination scope's
      // existing leaf names once. The queue's parallel uploads share the
      // set so they see each other's same-tick claims.
      const hasResolvedPaths = acceptedFiles.some((f) => f.resolvedPath);
      const taken = hasResolvedPaths
        ? new Set<string>()
        : await listExistingNamesForTarget(target);

      // Mint tempIds + abort controllers up front so we can enqueue all
      // tiles in a single batched `setQueue`, then run each file's
      // upload in parallel.
      const items = acceptedFiles.map((entry) => ({
        tempId: makeTempId(),
        entry,
        abortController: new AbortController(),
      }));

      setQueue((prev) => [
        ...prev,
        ...items.map(({ tempId, entry, abortController }) => ({
          tempId,
          file: entry.file,
          name:
            entry.displayName ??
            (entry.resolvedPath
              ? leafFromPath(entry.resolvedPath)
              : entry.file.name),
          destinationParentPath: parentPathOf(entry.resolvedPath),
          content_type:
            entry.contentType ||
            entry.file.type ||
            mime.lookup(entry.file.name) ||
            "",
          size: entry.file.size,
          status: "initializing" as UploadQueueItemStatus,
          kind: entry.kind ?? "create",
          abortController,
        })),
      ]);

      // Fan all items into the global upload slot pool. Each item's
      // body still resolves independently (so `onRecordCreated` and
      // queue-state transitions stay event-driven), but at most
      // `MAX_CONCURRENT` are actually inside `performFileRecordUpload`
      // at any given moment — the rest park in the FIFO waiter queue.
      // This caps the simultaneous load on `POST /file-records` and
      // S3 across all surfaces (workspace, canvas, chat) without
      // changing the queue's external semantics.
      let completedCount = 0;
      let failedCount = 0;
      await Promise.all(
        items.map(({ tempId, entry, abortController }) =>
          withUploadSlot(async () => {
            let createdRecordId: string | undefined;
            let createdRecord: FileRecord | undefined;

            try {
              const result = await performFileRecordUpload({
                file: entry.file,
                target,
                overwrite: entry.kind === "replace",
                resolvedPath: entry.resolvedPath,
                takenNames: hasResolvedPaths ? undefined : taken,
                signal: abortController.signal,
                queryClient,
                onRecordCreated: (record) => {
                  createdRecordId = record.id;
                  createdRecord = record;

                  // User cancelled between enqueue and the create response
                  // landing — tear down the zombie row and bail before S3.
                  // Only DELETE for `create` kinds; `replace` items reuse
                  // an existing row whose content is the user's file.
                  if (pendingCancellations.current.has(tempId)) {
                    pendingCancellations.current.delete(tempId);
                    if (entry.kind !== "replace") {
                      void orgAPI.fileRecords
                        .delete(record.id)
                        .catch(() => undefined);
                    }
                    abortController.abort();
                    return;
                  }

                  setQueue((prev) =>
                    prev.map((item) =>
                      item.tempId === tempId
                        ? {
                            ...item,
                            fileRecordId: record.id,
                            name: record.path.split("/").pop() || item.name,
                            content_type:
                              record.content_type || item.content_type,
                            size: record.size || item.size,
                            status:
                              record.status === "processed"
                                ? "processed"
                                : record.status === "processing"
                                  ? "processing"
                                  : "pending",
                          }
                        : item,
                    ),
                  );

                  options?.onRecordCreated?.(record, {
                    tempId,
                    meta: entry.meta,
                  });
                },
                onUploaded: (record) => {
                  setQueue((prev) =>
                    prev.map((item) =>
                      item.tempId === tempId
                        ? { ...item, s3Posted: true }
                        : item,
                    ),
                  );
                  options?.onUploaded?.(record, { tempId, meta: entry.meta });
                },
              });

              // Reflect post-HEIC name + image dims onto the tile in case
              // the helper picked up new values after the create-response
              // merge (e.g. HEIC convert renamed `.heic` → `.jpg`).
              setQueue((prev) =>
                prev.map((item) =>
                  item.tempId === tempId
                    ? {
                        ...item,
                        file: result.effectiveFile,
                        name: result.uniqueName || item.name,
                        content_type: result.effectiveType || item.content_type,
                        width: result.width,
                        height: result.height,
                      }
                    : item,
                ),
              );
              if (!abortController.signal.aborted) completedCount += 1;
            } catch (err) {
              // Abort-after-create path: the DELETE was already
              // dispatched in `removeItem`, don't resurrect the row as
              // an error.
              if (abortController.signal.aborted) return;

              failedCount += 1;
              const message =
                err instanceof Error ? err.message : "Upload failed";
              setQueue((prev) =>
                prev.map((item) =>
                  item.tempId === tempId
                    ? { ...item, status: "failed", error: message }
                    : item,
                ),
              );
              // Tear down the zombie row for `create` kinds — `replace`
              // items must preserve the user's existing record.
              if (createdRecordId && entry.kind !== "replace") {
                void orgAPI.fileRecords
                  .delete(createdRecordId)
                  .catch(() => undefined);
              }
              Sentry.captureException(err);
              options?.onUploadError?.(err, {
                tempId,
                meta: entry.meta,
                record: createdRecord,
              });
            }
          }),
        ),
      );

      if (completedCount > 0) {
        trackFileUploadCompleted({
          count: completedCount,
          destinationClass: target.class,
          method: options?.method,
        });
      }
      if (failedCount > 0) {
        trackFileUploadFailed({
          count: failedCount,
          destinationClass: target.class,
          method: options?.method,
        });
      }
    },
    [queryClient],
  );

  /**
   * Unified removal handler. Decision matrix:
   *
   *  | item state                       | local | API DELETE          | S3 abort |
   *  |----------------------------------|-------|---------------------|----------|
   *  | initializing (no fileRecordId)   |  drop | deferred (on resp)  |   yes    |
   *  | pending / processed / failed     |  drop | yes (create only)   |   yes    |
   *
   *  `replace` kinds NEVER trigger a DELETE — the row belongs to the
   *  user's existing file, and the partial S3 POST was atomic so the
   *  original object remains intact.
   */
  const removeItem = useCallback((tempId: string) => {
    let target: UploadQueueItem | undefined;
    setQueue((prev) => {
      target = prev.find((q) => q.tempId === tempId);
      return prev.filter((q) => q.tempId !== tempId);
    });
    if (!target) return;

    target.abortController.abort();

    // Only records THIS queue created are ours to clean up: "replace" reuses
    // the user's row, and an attached existing record was never created here
    // — deleting it would archive the actual workspace file.
    if (target.kind === "replace" || target.existingAttachment) return;

    if (target.fileRecordId) {
      void orgAPI.fileRecords
        .delete(target.fileRecordId)
        .catch(() => undefined);
    } else if (target.status === "initializing") {
      pendingCancellations.current.add(tempId);
    }
  }, []);

  const clear = useCallback(() => {
    setQueue([]);
  }, []);

  const enqueueExistingRecord = useCallback(
    (record: FileRecord) => {
      const leaf = record.path.split("/").filter(Boolean).pop() ?? record.path;
      const placeholder = new File([], leaf, { type: record.content_type });
      setQueue((prev) => [
        ...prev,
        {
          tempId: makeTempId(),
          file: placeholder,
          name: leaf,
          content_type: record.content_type,
          size: record.size ?? 0,
          width: record.width,
          height: record.height,
          status: "processed" as UploadQueueItemStatus,
          fileRecordId: record.id,
          s3Posted: true,
          kind: "create" as const,
          existingAttachment: true,
          previewUrl:
            pickFileRecordMediaURL(
              record as unknown as Parameters<typeof pickFileRecordMediaURL>[0],
              "thumbnail",
            ) ??
            record.url ??
            undefined,
          abortController: new AbortController(),
        },
      ]);
      queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
    },
    [queryClient],
  );

  return {
    queue,
    enqueue,
    enqueueExistingRecord,
    removeItem,
    clear,
  };
}

function makeTempId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function leafFromPath(path: string): string {
  return path.split("/").filter(Boolean).pop() ?? path;
}

/**
 * The parent directory portion of a resolved destination path. Empty
 * string for root (no slash), `"foo/"` for `"foo/bar.txt"`. Returns
 * `undefined` when the upload didn't ship a resolved path so callers
 * can distinguish "destined for root" from "destination unknown".
 */
function parentPathOf(resolvedPath: string | undefined): string | undefined {
  if (resolvedPath === undefined) return undefined;
  const idx = resolvedPath.lastIndexOf("/");
  return idx === -1 ? "" : resolvedPath.slice(0, idx + 1);
}
